import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import gameWeeksNBAUrl from 'components/card/Overlay/Stats/assets/stats/game_weeks_nba.svg';
import highestScoreUrl from 'components/card/Overlay/Stats/assets/stats/highest_score.svg';
import rewardsUrl from 'components/card/Overlay/Stats/assets/stats/rewards.svg';
import { drawHightlightedStat } from 'components/card/Overlay/Stats/drawHighlightedStat';
import { drawOwner } from 'components/card/Overlay/Stats/drawOwner';
import { drawPlayerName } from 'components/card/Overlay/Stats/drawPlayerName';
import { drawStat } from 'components/card/Overlay/Stats/drawStat';
import { loadTypekitFonts } from 'lib/fonts';
import { withFragments } from 'lib/gql';

import type { nbaStatsOverlay2024_AnyCardInterface } from './__generated__/index.graphql';

export const nbaStatsOverlay2024 = withFragments(
  (card_: nbaStatsOverlay2024_AnyCardInterface) => {
    const card = card_;
    if (!card.cardStats) {
      return undefined;
    }
    return async (image: ImageBitmap) => {
      const canvas = document.createElement('canvas');
      const WIDTH = image.width;
      const HEIGHT = image.height;

      canvas.width = WIDTH;
      canvas.height = HEIGHT;

      // not all card backs are the same size
      const scale = WIDTH / 771;

      const so5RewardsCount = card.cardStats?.so5RewardsCount ?? 0;
      const bestScore = Math.floor(
        card.cardStats?.bestSo5Appearance?.score ?? 0
      );

      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      // draw texture
      ctx.drawImage(image, 0, 0);

      const promises: Promise<void>[] = [];
      // load shuttleblock font
      await loadTypekitFonts('zuh8ukz');

      drawPlayerName({
        ctx,
        y: 443 * scale,
        x: WIDTH / 2,
        card,
        fontName: 'shuttleblock',
        scale,
      });

      promises.push(
        drawOwner({
          ctx,
          width: WIDTH,
          card,
          y: 549 * scale,
          scale,
          fontName: 'shuttleblock',
        })
      );

      //  Game weeks
      promises.push(
        drawStat({
          ctx,
          value: card.cardStats?.so5LineupsCount ?? 0,
          label: 'GAME WEEKS',
          iconUrl: gameWeeksNBAUrl,
          x: 33 * scale,
          y: 759 * scale,
          scale,
          fontName: 'shuttleblock',
        })
      );

      // Highest score
      promises.push(
        drawStat({
          ctx,
          value: bestScore,
          label: 'HIGHEST SCORE',
          iconUrl: highestScoreUrl,
          x: 285 * scale,
          y: 710 * scale,
          scale,
          fontName: 'shuttleblock',
        })
      );

      // Rewards
      promises.push(
        drawStat({
          ctx,
          value: so5RewardsCount,
          label: 'REWARDS',
          iconUrl: rewardsUrl,
          x: 555 * scale,
          y: 765 * scale,
          scale,
          fontName: 'shuttleblock',
        })
      );

      promises.push(
        drawHightlightedStat({
          ctx,
          card,
          x: 247 * scale,
          y: 913 * scale,
          scale,
          fontName: 'shuttleblock',
        })
      );
      await Promise.all(promises);

      return createImageBitmap(canvas);
    };
  },
  {
    AnyCardInterface: gql`
      fragment nbaStatsOverlay2024_AnyCardInterface on AnyCardInterface {
        slug

        cardStats {
          id
          so5LineupsCount
          so5RewardsCount
          bestSo5Appearance {
            id
            score
          }
        }
        ...drawOwner_AnyCardInterface
        ...drawPlayerName_AnyCardInterface
        ...drawHightlightedStat_AnyCardInterface
      }
      ${drawOwner.fragments.AnyCardInterface}
      ${drawPlayerName.fragments.AnyCardInterface}
      ${drawHightlightedStat.fragments.AnyCardInterface}
    ` as TypedDocumentNode<nbaStatsOverlay2024_AnyCardInterface>,
  }
);
